import { Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import i18n from "../i18n";

/**
 * Ask before doing something irreversible, and wait for the answer.
 *
 * What this replaces is `window.confirm`, which is wrong here twice over: it draws an OS
 * dialog in the middle of a dark-themed panel, in the browser's language rather than the
 * one the UI is in; and it blocks the main thread while it is open, which on this page
 * stops the timeline's animation frame and the push stream with it.
 *
 * A promise rather than the callback `openConfirmModal` takes, because every caller is
 * already inside an `await` chain and a callback would split each one in half.
 *
 * `onClose` runs on every exit -- Cancel, the close button, Escape, the backdrop -- while
 * `onConfirm` runs before the close it triggers. A promise keeps the first answer it is
 * given, so the confirming path resolves true and everything else resolves false.
 *
 * @param action Label for the confirming button. The verb it will do, not "OK": the button
 *   is the last thing read before an irreversible click, and it should say what happens.
 */
export function confirm(message: string, action: string, danger = false): Promise<boolean> {
  const { promise, resolve } = Promise.withResolvers<boolean>();
  modals.openConfirmModal({
    children: <Text size="sm">{message}</Text>,
    labels: { confirm: action, cancel: i18n.t("common.cancel") },
    confirmProps: { variant: "filled", color: danger ? "danger" : "accent" },
    onConfirm: () => resolve(true),
    onClose: () => resolve(false),
  });
  return promise;
}
